import { Button, Divider, Flex, Input, InputGroup, InputRightElement } from "@chakra-ui/react";
import { isNil } from "lodash/fp";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import { OFText } from "design_system/components/OFText";
import {
  CalculateOrderSummaryModel,
  PlatformType,
  RegionType,
  useApplyPromotionCodeForCartMutation
} from "graphql/generated";
import graphQLClient from "graphql/react-query";
import { colors } from "src/colors";
import {
  isPromotionAppliedState,
  isPromotionDisabledState,
  orderCalculationState,
  platformState,
  promoCodeState
} from "src/states";
import { CartSummaryInterface } from "src/types/cart.d";
import { checkCountry } from "src/utils/checkCountry";
import getNameProp from "src/utils/getNameProp";
import { formatPrice } from "src/utils/priceFormat";

type PromoType = "discount" | "cashback";

interface Props {
  t: (key: string, values?) => string;
  summary: CartSummaryInterface;
  items: any[];
  isCovidProd?: boolean;
  allowAction?: boolean;
}

const PromotionSection = ({
  t,
  summary,
  items,
  isCovidProd = false,
  allowAction = true
}: Props) => {
  const setOrderCalculation = useSetRecoilState(orderCalculationState);
  const [promoCode, setPromoCodeState] = useRecoilState(promoCodeState);
  const [isDisabled, setIsDisabled] = useRecoilState(isPromotionDisabledState);
  const [isPromotionApplied, setIsPromotionApplied] = useRecoilState(isPromotionAppliedState);
  const platform = useRecoilValue(platformState);

  const [promoCodeValue, setPromoCodeValue] = useState(promoCode?.code ?? "");
  const [errorMessage, setErrorMessage] = useState("");
  const [minSpend, setMinSpend] = useState("");
  const [disableCodeMessage, setDisableCodeMessage] = useState("");
  const previousSuccessPromoCode = useRef("");

  const {
    mutateAsync: applyPromotionCodeForCartMutation,
    error,
    isLoading
  } = useApplyPromotionCodeForCartMutation(graphQLClient);

  // temporary use to disable for shorterm renewal promo code
  const isAllowRenewalPromo = useMemo(() => {
    const hasRenewalOrder = items.some((order) => !isNil(order.oldOrder));

    return hasRenewalOrder && platform === PlatformType.Noah && checkCountry([RegionType.Hk]);
  }, [items, platform]);

  const onApplyPromo = useCallback(async () => {
    if (!allowAction) {
      return;
    }

    const resultApplyPromo = await applyPromotionCodeForCartMutation({
      input: {
        code: promoCodeValue
      }
    });

    if (error) {
      throw new Error("Can't apply promotion code for cart");
    }

    setIsPromotionApplied(true);

    const result = resultApplyPromo?.applyPromotionCodeForCart;

    const minAmount = result?.promotionCode?.restrictions?.minimum_amount;
    if (result && minAmount) setMinSpend(formatPrice(Math.abs(minAmount)));
    if (result?.errorCode) setErrorMessage(result.errorCode);
    if (result?.promotionCode) setPromoCodeState(result.promotionCode);
    if (result) setOrderCalculation(result.cart.calculation);

    if (!result.errorCode) {
      setErrorMessage("");
      previousSuccessPromoCode.current = promoCodeValue;

      if (result.promotionCode) {
        setIsDisabled(true);
      }
    }

    if (!result?.promotionCode) setErrorMessage("error-voucher-message");
  }, [
    allowAction,
    applyPromotionCodeForCartMutation,
    promoCodeValue,
    error,
    setIsPromotionApplied,
    setPromoCodeState,
    setOrderCalculation,
    setIsDisabled
  ]);

  /**
   * If has renew orders only => display message coupon-disable-for-renew-order
   * If has both renew & new orders => display message coupon-disable-for-mix-order
   * If no renew order => display promotion code input field
   * @param cartItems list of cartItems
   * @returns error message for coupon case
   */
  const getDisableCodeMessage = useCallback(
    (cartItems) => {
      let result = "";
      // get all items in cart has oldOrder
      const oldOrders = cartItems?.filter((val) => val.oldOrder);

      // all items have oldOrder => all are renewal orders
      if (oldOrders?.length === cartItems.length) {
        result = t("checkout:coupon-disable-for-renew-order");
      }

      // at least 1 item with oldOrder => include new & renew orders in cart
      else if (oldOrders.length > 0) {
        result = t("checkout:coupon-disable-for-mix-order");
      }

      return result;
    },
    [t]
  );

  const renderPromoLine = useCallback(
    (type: PromoType, orderSummary: CalculateOrderSummaryModel, promoCoupon: any): string => {
      const isCashback = type === "cashback";
      const promoAmount = isCashback ? orderSummary.cashbackEarned : orderSummary.discountAmountNow;
      const CashbackEarnedRate: string = promoCoupon?.metadata?.CashbackEarnedRate;

      if (!promoAmount || !promoCoupon || (isCashback && !CashbackEarnedRate)) {
        return "";
      }

      const amountText = formatPrice(Math.abs(promoAmount));

      const promoPercentOff = promoCoupon?.percent_off;

      const finalPercentOff = isCashback
        ? CashbackEarnedRate
          ? +CashbackEarnedRate * 100
          : null
        : promoPercentOff;

      const percentText = finalPercentOff
        ? t(`checkout:percent-${isCashback ? "cashback" : "off"}-label`, {
            percent_off: finalPercentOff
          })
        : "";

      return `${isCashback ? "+" : "-"}${amountText} ${percentText}`;
    },
    [t]
  );

  const renderPromoDetailText = useCallback((): string => {
    const promoTypes: PromoType[] = ["discount", "cashback"];

    const promoTexts = promoTypes.reduce((prev, cur) => {
      const text = renderPromoLine(cur, summary, promoCode.coupon);
      if (text) {
        return [...prev, text];
      }

      return [...prev];
    }, []);

    return promoTexts.join("\n");
  }, [summary, promoCode, renderPromoLine]);

  useEffect(() => {
    if (promoCode) onApplyPromo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAllowRenewalPromo) return setDisableCodeMessage(null);

    setDisableCodeMessage(getDisableCodeMessage(items));
  }, [isAllowRenewalPromo, items, t, getDisableCodeMessage]);

  const onResetPromo = async () => {
    const result = await applyPromotionCodeForCartMutation({
      input: {
        code: "",
        previousCode: previousSuccessPromoCode.current
      }
    });

    if (result?.applyPromotionCodeForCart) {
      setPromoCodeState(result.applyPromotionCodeForCart.promotionCode);
      setOrderCalculation(result.applyPromotionCodeForCart?.cart?.calculation);
      setIsDisabled(false);
      setIsPromotionApplied(false);
    }
  };

  if (items?.length === 0) return null;

  if (disableCodeMessage) {
    return (
      <Flex
        direction="column"
        my="12px"
        {...getNameProp("disable-promo-section")}
        bg="primaryTwo.500"
        borderRadius="8px"
        padding="18px"
        mb="20px"
      >
        <OFText fontFamily="body" fontSize={{ base: "md" }} color="white">
          {disableCodeMessage}
        </OFText>
      </Flex>
    );
  }

  return (
    <Flex direction="column" my="12px" {...getNameProp("prescription-promo-section")}>
      {isCovidProd && (
        <Flex justifyContent="space-between" fontWeight="bold" {...getNameProp("gst")}>
          <OFText>{t("checkout:gst-label")}</OFText>
          <OFText>{t("checkout:gst-included-label")}</OFText>
        </Flex>
      )}
      <InputGroup
        mt={[isCovidProd ? "4" : undefined]}
        size="md"
        bg={colors.white.default}
        borderRadius="10px"
        height="50px"
      >
        <Input
          placeholder={t("checkout:add-a-code-placeholder")}
          border="none"
          borderRadius="8px"
          color={colors.orange[400]}
          fontWeight="bold"
          _placeholder={{
            color: "placeholderInDarkTextBox",
            fontWeight: "bold",
            fontSize: "sm"
          }}
          height="50px"
          value={promoCodeValue}
          onChange={(event) => {
            setPromoCodeValue(event.target.value);
            if (event.target.value === "" && isPromotionApplied) {
              onResetPromo();
            }
          }}
          fontSize="16px"
          disabled={!allowAction}
        />
        {allowAction && (
          <InputRightElement width="5.5rem" height="100%">
            <Button
              fontFamily="graphik"
              fontSize="xs"
              color="white"
              bg={colors.orange[400]}
              size="sm"
              rounded="2xl"
              width="65px"
              isDisabled={isDisabled || items?.length === 0}
              onClick={() => onApplyPromo()}
              isLoading={isLoading}
            >
              {isDisabled ? t("checkout:added-label") : t("checkout:add-label")}
            </Button>
          </InputRightElement>
        )}
      </InputGroup>
      {isPromotionApplied && (
        <>
          {isNil(promoCode) || errorMessage ? (
            <OFText textStyle="body3" mt="8px">
              {t(`checkout:${errorMessage}`, { minSpend })}
            </OFText>
          ) : (
            <Flex justifyContent="space-between" fontWeight="bold" mt="8px">
              <OFText textStyle="body3">{t("checkout:promo-code-label")}</OFText>

              <OFText textStyle="body3" align="right">
                {renderPromoDetailText()}
              </OFText>
            </Flex>
          )}
        </>
      )}

      <Divider m="12px 0 0 0" borderColor={colors.grey[300]} />
    </Flex>
  );
};

export default PromotionSection;
